import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPrint, faExpand, faCompress, faUpRightFromSquare, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import type { OperationsInventoryRow } from '../data/timEInventoryList';
import './MonthlyInventoryReportModal.css';

function parseMoney(s: string): number {
  return Number(String(s).replace(/[$,]/g, '').trim()) || 0;
}

function formatUsd(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function escapeCsvCell(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function escapeHtml(v: string): string {
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildCsv(headers: string[], rows: string[][]): string {
  const lines = [headers.map(escapeCsvCell).join(',')];
  for (const row of rows) {
    lines.push(row.map(escapeCsvCell).join(','));
  }
  return lines.join('\r\n');
}

const REPORT_HEADERS = ['Product', 'Available', 'Replacement Cost per item', 'Value'];

function displayCell(v: string): string {
  return String(v ?? '').trim();
}

function displayCount(v: string): string {
  const d = displayCell(v);
  return d === '' ? '0' : d;
}

/** Value = Available × replacement cost when a cost is set; otherwise keep the stored value. */
function rowValue(r: OperationsInventoryRow): string {
  const cost = parseMoney(r.replacementCostPerItem);
  if (cost > 0) return formatUsd(parseMoney(r.available) * cost);
  return displayCell(r.value);
}

function rowToDisplayCells(r: OperationsInventoryRow): string[] {
  return [displayCell(r.product), displayCount(r.available), displayCell(r.replacementCostPerItem), rowValue(r)];
}

export interface FfeInventoryReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: OperationsInventoryRow[];
  reportDate?: Date;
}

/**
 * FF&E report: the same print / CSV / full-screen / new-tab toolbar as the monthly
 * inventory report, with the four columns the FF&E table already uses.
 */
const FfeInventoryReportModal: React.FC<FfeInventoryReportModalProps> = ({
  isOpen,
  onClose,
  items,
  reportDate = new Date(),
}) => {
  const monthName = useMemo(
    () => new Intl.DateTimeFormat('en-US', { month: 'long' }).format(reportDate),
    [reportDate]
  );
  const year = reportDate.getFullYear();
  const reportTitle = `${monthName} ${year} FF&E Inventory`;

  const dataRows = useMemo(
    () => items.filter((r) => displayCell(r.product) !== ''),
    [items]
  );

  const totalValue = useMemo(
    () => dataRows.reduce((sum, r) => sum + parseMoney(rowValue(r)), 0),
    [dataRows]
  );

  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [fullScreen, setFullScreen] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  const toggleSort = useCallback((col: number) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }, [sortCol]);

  const sortedRows = useMemo(() => {
    if (sortCol === null) return dataRows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...dataRows].sort((a, b) => {
      const ca = rowToDisplayCells(a)[sortCol];
      const cb = rowToDisplayCells(b)[sortCol];
      const na = parseMoney(ca);
      const nb = parseMoney(cb);
      const aNum = ca.trim() !== '' && !Number.isNaN(Number(String(ca).replace(/[$,]/g, '').trim()));
      const bNum = cb.trim() !== '' && !Number.isNaN(Number(String(cb).replace(/[$,]/g, '').trim()));
      const cmp = aNum && bNum ? na - nb : ca.localeCompare(cb, undefined, { numeric: true, sensitivity: 'base' });
      return cmp * dir;
    });
  }, [dataRows, sortCol, sortDir]);

  useEffect(() => {
    if (!downloadMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target as Node)) {
        setDownloadMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDownloadMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [downloadMenuOpen]);

  const handleClose = useCallback(() => {
    setFullScreen(false);
    setDownloadMenuOpen(false);
    setSortCol(null);
    setSortDir('asc');
    onClose();
  }, [onClose]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const buildReportHtml = useCallback(() => {
    const headRow = REPORT_HEADERS.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
    const bodyRows = dataRows
      .map((r) => `<tr>${rowToDisplayCells(r).map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
      .join('');
    const totalRow =
      `<tr class="total-row"><td colspan="3">Total</td>` +
      `<td>${totalValue ? escapeHtml(formatUsd(totalValue)) : ''}</td></tr>`;

    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(reportTitle)}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 24px; color: #1f2933; }
  h1 { font-size: 1.5rem; margin: 0 0 1rem; }
  table { border-collapse: collapse; width: 100%; font-size: 0.85rem; }
  th, td { border: 1px solid #d2d6dc; padding: 6px 8px; text-align: left; }
  thead th { background: #f5f7fa; position: sticky; top: 0; cursor: pointer; user-select: none; white-space: nowrap; }
  thead th:hover { background: #e4e8ec; }
  thead th .sort-ind { font-size: 0.75em; color: #2c7a3f; margin-left: 2px; }
  tfoot .total-row td { font-weight: 700; background: #f5f7fa; }
  @media print { body { margin: 0; } thead th { cursor: default; } }
</style>
</head>
<body>
<h1>${escapeHtml(reportTitle)}</h1>
<table>
<thead><tr>${headRow}</tr></thead>
<tbody>${bodyRows}</tbody>
<tfoot>${totalRow}</tfoot>
</table>
<script>
(function () {
  var table = document.querySelector('table');
  if (!table || !table.tHead || !table.tBodies[0]) return;
  var tbody = table.tBodies[0];
  var headers = table.tHead.rows[0].cells;
  var state = { col: -1, dir: 1 };
  function num(s) { var n = parseFloat(String(s).replace(/[$,]/g, '')); return isNaN(n) ? null : n; }
  function cellText(row, i) { return row.cells[i] ? row.cells[i].textContent.trim() : ''; }
  for (var i = 0; i < headers.length; i++) {
    (function (idx) {
      var th = headers[idx];
      var ind = document.createElement('span');
      ind.className = 'sort-ind';
      th.appendChild(ind);
      th.addEventListener('click', function () {
        if (state.col === idx) { state.dir = -state.dir; } else { state.col = idx; state.dir = 1; }
        var rows = Array.prototype.slice.call(tbody.rows);
        rows.sort(function (a, b) {
          var va = cellText(a, idx), vb = cellText(b, idx);
          var na = num(va), nb = num(vb), cmp;
          if (na !== null && nb !== null) { cmp = na - nb; }
          else { cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }); }
          return cmp * state.dir;
        });
        rows.forEach(function (r) { tbody.appendChild(r); });
        for (var h = 0; h < headers.length; h++) {
          var s = headers[h].querySelector('.sort-ind');
          if (s) s.textContent = '';
        }
        ind.textContent = state.dir > 0 ? ' \u25B2' : ' \u25BC';
      });
    })(i);
  }
})();
</script>
</body>
</html>`;
  }, [dataRows, reportTitle, totalValue]);

  const handleOpenInNewTab = useCallback(() => {
    const html = buildReportHtml();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      URL.revokeObjectURL(url);
      alert('Please allow pop-ups to open the report in a new tab.');
      return;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, [buildReportHtml]);

  const handleDownloadCsv = useCallback(
    (mode: 'static' | 'formulas') => {
      let bodyRows: string[][];
      let totalRow: string[];

      if (mode === 'formulas') {
        bodyRows = dataRows.map((r, i) => {
          const rowNum = i + 2;
          const cost = parseMoney(r.replacementCostPerItem);
          return [
            displayCell(r.product),
            String(parseMoney(r.available)),
            cost > 0 ? String(cost) : '',
            cost > 0 ? `=B${rowNum}*C${rowNum}` : displayCell(r.value),
          ];
        });
        const hasRows = dataRows.length > 0;
        totalRow = [
          'Total',
          '',
          '',
          hasRows ? `=SUM(D2:D${dataRows.length + 1})` : '',
        ];
      } else {
        bodyRows = dataRows.map((r) => rowToDisplayCells(r));
        totalRow = ['Total', '', '', totalValue ? String(Math.round(totalValue)) : ''];
      }

      const csv = buildCsv(REPORT_HEADERS, [...bodyRows, totalRow]);
      const safeFile = `${monthName}-${year}-FFE-Inventory`.replace(/[/\\?%*:|"<>]/g, '-');
      const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeFile}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [dataRows, monthName, year, totalValue]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={reportTitle}
      extraWide={!fullScreen}
      fullScreen={fullScreen}
      contentClassName="inventory-monthly-report-print-scope"
    >
      <div className="modal-body inventory-monthly-report-body">
        <p className="inventory-monthly-report-lead">
          Furniture, fixtures, and equipment from the FF&amp;E inventory. Value = Available × replacement
          cost when a replacement cost is set.
        </p>
        <div className="inventory-report-toolbar no-print">
          <button type="button" className="page-toolbar-btn" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} style={{ marginRight: '0.35rem' }} aria-hidden />
            Print
          </button>
          <div className="inventory-report-download" ref={downloadMenuRef}>
            <button
              type="button"
              className="page-toolbar-btn"
              onClick={() => setDownloadMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={downloadMenuOpen}
            >
              <FontAwesomeIcon icon={faDownload} style={{ marginRight: '0.35rem' }} aria-hidden />
              Download
              <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: '0.4rem', fontSize: '0.75em' }} aria-hidden />
            </button>
            {downloadMenuOpen && (
              <div className="inventory-report-download-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="inventory-report-download-item"
                  onClick={() => {
                    setDownloadMenuOpen(false);
                    handleDownloadCsv('static');
                  }}
                >
                  <span className="inventory-report-download-item-title">Static values</span>
                  <span className="inventory-report-download-item-sub">Fixed numbers as shown</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="inventory-report-download-item"
                  onClick={() => {
                    setDownloadMenuOpen(false);
                    handleDownloadCsv('formulas');
                  }}
                >
                  <span className="inventory-report-download-item-title">Formulas</span>
                  <span className="inventory-report-download-item-sub">Live spreadsheet formulas</span>
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="page-toolbar-btn"
            onClick={() => setFullScreen((v) => !v)}
            aria-pressed={fullScreen}
          >
            <FontAwesomeIcon icon={fullScreen ? faCompress : faExpand} style={{ marginRight: '0.35rem' }} aria-hidden />
            {fullScreen ? 'Exit full screen' : 'Full screen'}
          </button>
          <button type="button" className="page-toolbar-btn" onClick={handleOpenInNewTab}>
            <FontAwesomeIcon icon={faUpRightFromSquare} style={{ marginRight: '0.35rem' }} aria-hidden />
            Open in new tab
          </button>
        </div>
        <div className="inventory-monthly-report-table-wrap">
          <table className="inventory-monthly-report-table">
            <thead>
              <tr>
                {REPORT_HEADERS.map((h, idx) => (
                  <th
                    key={h}
                    className="inventory-monthly-report-sortable-th"
                    onClick={() => toggleSort(idx)}
                    aria-sort={sortCol === idx ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    {h}
                    <span className="inventory-monthly-report-sort-indicator" aria-hidden>
                      {sortCol === idx ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={REPORT_HEADERS.length}>No FF&amp;E items selected.</td>
                </tr>
              ) : (
                sortedRows.map((r, i) => (
                  <tr key={`${r.product}-${i}`}>
                    {rowToDisplayCells(r).map((cell, j) => (
                      <td key={j}>{cell}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="inventory-monthly-report-total-row">
                <td colSpan={3}>Total</td>
                <td>{totalValue ? formatUsd(totalValue) : ''}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div className="modal-actions no-print">
        <button type="button" className="cancel-button" onClick={handleClose}>
          Close
        </button>
      </div>
    </Modal>
  );
};

export default FfeInventoryReportModal;
